import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { IoStarOutline } from '@react-icons/all-files/io5/IoStarOutline';
import { BucketLogData } from '@bucketplace/bucket-log';
import { useIsInApp, useSnackbar } from '@bucketplace/design-system';
import { Modal } from '@bucketplace/design-system/bds';

import { EditorPlaceholder } from '@/components';
import { useEditorInfoContext, useRaffleContext } from '@/contexts';
import { useEditorNode } from '@/hooks';
import { DocumentPath } from '@/types';

import { RaffleLayout } from './RaffleLayout';
import { RaffleEntryResponse, RepresentativeRaffleEvent } from './types';

export interface RaffleViewProps {
  /** 래플 그룹 ID */
  groupId: string;
  /** 표시 날짜 (어드민에서 특정 날짜 기준으로 미리보기) */
  displayDate?: Date;
  /** 로그 데이터 */
  logData?: BucketLogData;
}
export interface RaffleProps {
  /** 래플 그룹 ID */
  groupId: string;
  path: DocumentPath;
  /** 로그 데이터 */
  logData?: BucketLogData;
}

export function Raffle({
  groupId,
  path,
  logData,
}: RaffleProps): ReactElement {
  const { displayDate } = useEditorNode(path);
  const isInApp = useIsInApp(location);

  const { getRaffleEvent, postRaffleEntry } = useRaffleContext();
  const { onSendLog } = useEditorInfoContext();
  const { open: openSnackbar } = useSnackbar();

  const [raffleEvent, setRaffleEvent] = useState<RepresentativeRaffleEvent | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [entryResult, setEntryResult] = useState<RaffleEntryResponse | null>(null);

  useEffect(() => {
    if (!groupId || groupId.trim() === '' || getRaffleEvent == null) {
      return;
    }

    const fetch = async (): Promise<void> => {
      try {
        const event = await getRaffleEvent(groupId, displayDate);
        setRaffleEvent(event);
      } catch (error) {
        console.error('래플 이벤트 조회 실패:', error);
        setRaffleEvent(null);
      }
    };

    fetch();
  }, [groupId, displayDate, getRaffleEvent]);

  const handleRaffleEntry = async (): Promise<void> => {
    if (!postRaffleEntry) {
      return;
    }

    if (logData != null && onSendLog != null) {
      onSendLog(logData);
    }

    try {
      const result = await postRaffleEntry(raffleEvent?.couponRaffleEvent.id ?? '');

      if (result == null) {
        return;
      }

      setEntryResult(result);
      setShowModal(true);
    } catch (error) {
      openSnackbar({ content: '쿠폰 발급에 실패했습니다. 다시 시도해주세요.' });
      console.error('래플 참여 실패:', error);
    }
  };

  const closeModal = (): void => {
    setShowModal(false);
    setEntryResult(null);
  };

  const modalContent = useMemo(() => {
    if (!entryResult) {
      return '';
    }

    switch (entryResult.result) {
      case 'WIN':
        return entryResult.couponMold?.name
          ? `축하합니다! ${entryResult.couponMold.name}에 당첨됐어요!`
          : '축하합니다! 당첨됐어요!';
      case 'LOSS':
        return '아쉽지만 이번에는 당첨되지 않았어요. 다음 기회에 다시 도전해주세요!';
      case 'DUPLICATE':
        return '이미 응모가 완료됐어요. 다음 기회에 또 도전해주세요!';
      case 'BEFORE_START':
        return '아직 이벤트 시작 전입니다. 이벤트 시작 후 다시 응모해주세요.';
      case 'CLOSED':
        return '이벤트가 종료되었습니다.';
      default:
        return '';
    }
  }, [entryResult]);

  const commonModalProps = useMemo(() => ({
    header: {
      title: '참여 완료',
      onClose: closeModal,
    },
    footer: {
      action: {
        primary: {
          text: '확인',
          onClick: closeModal,
        },
      },
    },
    onBlur: closeModal,
    children: <p>{ modalContent }</p>,
  }), [modalContent]);

  const isButtonDisabled = !raffleEvent?.isEnterable;
  const imageUrl = raffleEvent?.couponRaffleEvent.imageUrl;
  const videoUrl = raffleEvent?.couponRaffleEvent.videoUrl;

  if (imageUrl == null) {
    return (
      <EditorPlaceholder
        icon={<IoStarOutline />}
        message="래플 이미지를 불러올 수 없습니다."
      />
    );
  }

  return (
    <>
      <RaffleLayout
        imageUrl={imageUrl ?? ''}
        videoUrl={videoUrl}
        buttonText={raffleEvent?.couponRaffleEvent.buttonText}
        buttonBottom={70}
        onButtonClick={handleRaffleEntry}
        disabled={isButtonDisabled}
      />
      { showModal && isInApp && (
        <Modal
          width={300}
          isPopupInMobile
          {...commonModalProps}
        >
          <p css={css`padding: 0 16px;`}>{ modalContent }</p>
        </Modal>
      ) }
      { showModal && !isInApp && (
        <Modal
          width={500}
          {...commonModalProps}
        >
          <p css={css`padding: 0 16px;`}>{ modalContent }</p>
        </Modal>
      ) }
    </>
  );
}
